import { useFormikContext } from "formik";
import Modal from "../../Modal";
import AllegedAbuseFlag from "./AllegedAbuseFlag";
import EMRReminderModal from "./EMRReminderModal";
import SaveOrDiscardPrompt from "./SaveOrDiscardPrompt";
import DeleteDraft from "./DeleteDraft";
import ButtonGroup from "./ButtonGroup";
import {
  FormHeader,
  IncidentSummaryGrid,
  IncidentSummaryItem,
  IncidentSummaryLabel,
  IncidentSummaryValue,
} from "./styles";
import { PageTitle } from "../../../screens/Incidents/Forms/formStyles.style";
import { IncidentNumber } from "../../../screens/Incidents/approvals/component/approval-list/style";
import LastModifiedDate from "./LastModifiedDate";
import { useIncidentFormContext } from "./IncidentContext";
import { IncidentStatus } from "../../../types/fieldInstance.interface";
import BottomButtonGroup from "./BottomButtonGroup";
import FormSection from "./FormSection";
import { PLACEHOLDER_VALUE } from "../../../shared/sharedConstants";
import { useIncidentData } from "../../../layouts/incidentLayout";
import { resetFormOnAction, saveIncidentForm } from "./methodsAndHandlers";
import { stages } from "../../../utils/helpers/incidentFormTabs";
import Spinner from "react-spinner-material";
import AskConfirmation from "./AskConfirmation";
import HighSeverityFlag from "./HighSeverityFlag";
import HighSeverityConfirm from "./HighSeverityConfirm";
import { SectionProvider } from "../context/SectionContext";
import { formatDisplayDateTime } from "../dateTimeInput/methods";
import MarkReadyConfirmModal from "../../MarkReadyConfirmModal/MarkReadyConfirmModal";
import { markIncidentReadyForApproval } from "../../../api/incident/apiIncidents";
import { queryClient } from "../../../App";
import { message } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

interface IncidentFormComponentType {
  incidentStatus?: IncidentStatus;
  tabTitle: string;
  _handleDiscard: any;
  sections: any;
}

const IncidentFormComponent = ({
  incidentStatus,
  tabTitle,
  _handleDiscard,
  sections,
}: IncidentFormComponentType) => {
  const {
    showPrompt,
    openDeleteModal,
    setOpenDeleteModal,
    allFormFields,
    formStateValues,
    selectOptions,
    canSubmitForm,
    tabState,
    shouldAllowInput,
    showConfirmationPrompt,
    setShowConfirmationPrompt,
    markedAsHighSeverity,
    editPermission,
    showHighSeverityModal,
    showEMRReminder,
    setShowEMRReminder,
    handleEMRAcknowledgement: contextHandleEMRAcknowledgement,
  } = useIncidentFormContext();

  const formik = useFormikContext<{ [x: string]: any }>();
  const {
    selectedIncident,
    showAllegedAbuseFlag,
    setShowAllegedAbuseFlag,
    incidentTypeObj,
    setIncidentFormState,
    setIncidentFormData,
    mergeIncidentTabState,
    saveMutateAsync,
    createIncidentLoading,
  } = useIncidentData();

  const [showMarkReadyConfirm, setShowMarkReadyConfirm] = useState(false);
  const [markReadyLoading, setMarkReadyLoading] = useState(false);
  const navigate = useNavigate();
  

  const formPageTitle = `${incidentTypeObj?.label ?? ""} - ${tabTitle}`;

  const isDeletedIncident =
    selectedIncident?.Enabled_Incident_For_Web_User__c === false;

  function extractId(inputString: string): string {
    // Define the regex pattern to match the template and capture the Id
    const pattern =
      /^Incident draft of Id: (\w{18}) was saved successfully!!!$/;

    // Test the string against the pattern
    const match = inputString.match(pattern);

    // If the pattern matches, return the captured Id, otherwise return null
    if (match) {
      return match[1];
    } else {
      return "";
    }
  }

  function onSaveSuccess(values: any, res: any) {
    const draftId = extractId(res.data.message);
    setIncidentFormState({
      hasUnsavedChanges: false,
      incidentStatus: IncidentStatus.Draft,
      draftId: draftId,
    });
    resetFormOnAction(
      formik.resetForm,
      values,
      tabState,
      mergeIncidentTabState,
      setIncidentFormData
    );
  }

  async function _saveIncidentForm() {
    return saveIncidentForm(
      formik.values,
      formStateValues,
      allFormFields,
      { stages, tabTitle: tabTitle },
      selectOptions,
      saveMutateAsync,
      onSaveSuccess
    );
  }

  function getSubmitButtonName(color?: string, radius?: number) {
    if (selectedIncident && incidentStatus !== IncidentStatus.Draft) {
      return createIncidentLoading ? (
        <Spinner
          radius={radius ?? 24}
          color={color ?? "var(--primaryPurpleMain)"}
          stroke={2}
          visible={true}
        />
      ) : (
        "Resubmit"
      );
    } else {
      return createIncidentLoading ? (
        <Spinner
          radius={radius ?? 24}
          color={color ?? "var(--primaryPurpleMain)"}
          stroke={2}
          visible={true}
        />
      ) : (
        "Submit"
      );
    }
  }

  function isCreateButtonDisabled() {
    if (!shouldAllowInput || !canSubmitForm || isDeletedIncident) return true;
    return createIncidentLoading;
  }

  function shouldShowSaveButton() {
    if (selectedIncident) {
      return incidentStatus === IncidentStatus.Draft && !isDeletedIncident;
    }
    return true;
  }

  function shouldShowIncidentNumber() {
    return !!incidentStatus && incidentStatus !== IncidentStatus.Draft;
  }

  function onHighSeverityToggle() {
    setIncidentFormState({
      markedAsHighSeverity: !markedAsHighSeverity,
      markedHighSeverityInSession: !markedAsHighSeverity,
    });
  }

  async function handleConfirmMarkReady() {
    setMarkReadyLoading(true);
    try {
      await markIncidentReadyForApproval(selectedIncident?.Id);
      message.success("Incident marked as Ready for Approval");
      queryClient.invalidateQueries(["incidentsWithParams"]);
      queryClient.invalidateQueries(["incident", selectedIncident?.Id]);
      queryClient.invalidateQueries(["incidentDetailsWithId"]);
      setShowMarkReadyConfirm(false);
    } catch (err: any) {
      console.error("Error marking incident ready:", err);
      message.error(err?.response?.data?.message || "Failed to mark incident as Ready for Approval");
    } finally {
      setMarkReadyLoading(false);
    }
  }

  

  function handleCloseMarkReadyModal() {
    setShowMarkReadyConfirm(false);
  }

  function handleEMRAcknowledgement() {
    // Close EMR modal
    setShowEMRReminder(false);
    
    // Check if incident is Submitted and has a conclusion (check from form values that were just submitted)
    const hasConclusion = formik.values.Conclusion__c?.trim();
    if (selectedIncident?.Incident_Status__c === IncidentStatus.Submitted && hasConclusion) {
      setShowMarkReadyConfirm(true);
    }
    
    // Call the original context handler for navigation and cleanup
    contextHandleEMRAcknowledgement();

    // If the user was editing an incident that was in "Ready for Approval" status
    // after submitting, redirect them to the approvals page to encourage sign-off
    const incidentId = selectedIncident?.Id;
    const currentStatus = selectedIncident?.Incident_Status__c || incidentStatus;
    if (incidentId && currentStatus === IncidentStatus.ReadyForApproval) {
      navigate(`/incidents/${incidentId}/approvals?mode=view`);
    }
  }

  const residentName =
    selectedIncident?.Resident_Name__c ||
    PLACEHOLDER_VALUE;

  const rawIncidentDateTime =
    selectedIncident?.Incident_Date_Time__c ||
    null;

  const formattedIncidentDateTime = formatDisplayDateTime(rawIncidentDateTime);

  const statusDisplay =
    selectedIncident?.Incident_Status__c ||
    PLACEHOLDER_VALUE;

  const incidentSummaryItems = [
    { label: "Resident Name", value: residentName },
    { label: "Date/Time of Incident", value: formattedIncidentDateTime },
    { label: "Status", value: statusDisplay },
  ];

  const shouldShowIncidentSummary = incidentSummaryItems.some(
    (item) => item.value && item.value !== PLACEHOLDER_VALUE
  );

  return (
    <>
      <Modal open={showAllegedAbuseFlag}>
        <AllegedAbuseFlag setShowAllegedAbuseFlag={setShowAllegedAbuseFlag} />
      </Modal>
      <Modal open={showHighSeverityModal}>
        <HighSeverityConfirm />
      </Modal>
      <Modal open={showEMRReminder} close={handleEMRAcknowledgement}>
        <EMRReminderModal setShowEMRReminder={setShowEMRReminder} onAcknowledge={handleEMRAcknowledgement} />
      </Modal>
      <MarkReadyConfirmModal
        open={showMarkReadyConfirm}
        onClose={handleCloseMarkReadyModal}
        onMarkReady={handleConfirmMarkReady}
        confirmLoading={markReadyLoading}
      />
      <Modal open={showPrompt}>
        <SaveOrDiscardPrompt
          saveForm={_saveIncidentForm}
          isCreateButtonDisabled={isCreateButtonDisabled()}
          canSaveForm={shouldShowSaveButton()}
          submitForm={() => formik.submitForm()}
          discardForm={_handleDiscard}
          getSubmitButtonName={getSubmitButtonName}
        />
      </Modal>
      <Modal open={showConfirmationPrompt}>
        <AskConfirmation
          closeAskConfirmationModal={setShowConfirmationPrompt}
        />
      </Modal>
      <Modal open={openDeleteModal}>
        <DeleteDraft closeDeleteModal={setOpenDeleteModal} />
      </Modal>
      <ButtonGroup
        saveIncidentForm={_saveIncidentForm}
        isCreateButtonDisabled={isCreateButtonDisabled()}
        shouldShowSaveButton={shouldShowSaveButton()}
        handleOpenModal={setOpenDeleteModal}
        getSubmitButtonName={getSubmitButtonName}
        incidentStatus={incidentStatus}
        tabTitle={tabTitle}
      />
      <FormHeader>
        <div>
          <PageTitle>{formPageTitle}</PageTitle>
          {editPermission && (
            <HighSeverityFlag
              name={"Mark_as_High_Severity__c"}
              value={markedAsHighSeverity}
              onChange={onHighSeverityToggle}
              disabled={!shouldAllowInput}
            />
          )}
        </div>
        <div>
          {shouldShowIncidentNumber() && (
            <IncidentNumber style={{ textAlign: "right" }}>
              Incident Number: {selectedIncident?.Incident_Name__c}
            </IncidentNumber>
          )}
          <LastModifiedDate
            lastModifiedDate={selectedIncident?.LastModifiedDate}
          />
        </div>
      </FormHeader>
      {shouldShowIncidentSummary && (
        <IncidentSummaryGrid data-testid="incident-summary-grid">
          {incidentSummaryItems.map(({ label, value }) => (
            <IncidentSummaryItem key={label}>
              <IncidentSummaryLabel>{label}</IncidentSummaryLabel>
              <IncidentSummaryValue>{value}</IncidentSummaryValue>
            </IncidentSummaryItem>
          ))}
        </IncidentSummaryGrid>
      )}
      <form onSubmit={formik.handleSubmit}>
        <SectionProvider>
          <FormSection sections={sections} />
        </SectionProvider>
        <BottomButtonGroup
          tabTitle={tabTitle}
          incidentStatus={incidentStatus}
          isCreateButtonDisabled={isCreateButtonDisabled()}
          isDeletedIncident={isDeletedIncident}
        />
      </form>
    </>
  );
};

export default IncidentFormComponent;
